import React, { useEffect, useRef } from "react";

// Renders a <video> through a WebGL green-screen key so the avatar stands directly on the page with no
// background — the technique Anam documents (and Docket's "Frameless" agent uses). The source video stays
// in the DOM (hidden) for audio playback; this canvas is what the visitor sees.
// Shader adapted from Anam's cookbook: anam.ai/cookbook/transparent-backgrounds-canvas-greenscreen

const VERTEX = `
attribute vec2 a_position;
varying vec2 v_texCoord;
void main() {
  v_texCoord = vec2((a_position.x + 1.0) / 2.0, 1.0 - (a_position.y + 1.0) / 2.0);
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const FRAGMENT = `
precision mediump float;
uniform sampler2D u_source;
uniform float u_minGreen;
uniform float u_greenBias;
uniform float u_softness;
uniform float u_spill;
varying vec2 v_texCoord;
void main() {
  vec4 texel = texture2D(u_source, v_texCoord);
  float red = texel.r * 255.0;
  float green = texel.g * 255.0;
  float blue = texel.b * 255.0;
  float minChannel = min(red, min(green, blue));
  float maxRedBlue = max(red, blue);
  float maxChannel = max(red, max(green, blue));
  float saturation = maxChannel == 0.0 ? 0.0 : (maxChannel - minChannel) / maxChannel;
  float greenDominance = green - maxRedBlue;
  float keyRamp = max(8.0, u_softness * 0.55);
  bool isGreen = green == maxChannel && green > u_minGreen && green > red * u_greenBias && green > blue * u_greenBias && saturation > 0.08 && greenDominance > 2.0;
  vec3 color = texel.rgb;
  float alpha = texel.a;
  if (isGreen) {
    float keyedAmount = clamp((greenDominance - 2.0) / keyRamp + (saturation - 0.08) * 1.8, 0.0, 1.0);
    alpha = texel.a * (1.0 - keyedAmount);
  } else if (greenDominance > 8.0 && green > 70.0) {
    color.g = max(0.0, green - greenDominance * u_spill) / 255.0;
  }
  gl_FragColor = vec4(color * alpha, alpha);
}`;

// Tuned for a typical studio green; adjust if the edges of the custom avatar look fringed.
const SETTINGS = { minGreen: 60, greenBias: 1.15, softness: 40, spill: 0.6 };

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader));
  return shader;
}

const ChromaKeyCanvas = ({ videoRef, className }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas?.getContext("webgl", { premultipliedAlpha: true, alpha: true });
    if (!gl) return undefined;

    const program = gl.createProgram();
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uniforms = Object.fromEntries(
      ["minGreen", "greenBias", "softness", "spill"].map((k) => [k, gl.getUniformLocation(program, `u_${k}`)]),
    );

    let frame;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) return;
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      } catch {
        return; // cross-origin frame without CORS — nothing we can draw
      }
      gl.uniform1f(uniforms.minGreen, SETTINGS.minGreen);
      gl.uniform1f(uniforms.greenBias, SETTINGS.greenBias);
      gl.uniform1f(uniforms.softness, SETTINGS.softness);
      gl.uniform1f(uniforms.spill, SETTINGS.spill);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    draw();

    return () => {
      cancelAnimationFrame(frame);
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, [videoRef]);

  return <canvas ref={canvasRef} className={className} aria-hidden />;
};

export default ChromaKeyCanvas;
